import * as React from "react";

/**
 * Use a value that must be awaited before it is available.
 *
 * If `deps` change, the previous request is abandoned, and `getValue` is
 * called again. These calls are not throttled, so make sure `deps` is as
 * specific as possible to avoid calling `getValue` more than necessary!
 */
const useAwaitedValue = <T>(
  initialValue: T | (() => T),
  getValue: () => Promise<React.SetStateAction<T>>,
  deps: React.DependencyList,
) => {
  const [value, setValue] = React.useState(initialValue);
  React.useEffect(() => {
    let shouldSetValue = true;
    (async () => {
      const newValue = await getValue();
      if (shouldSetValue) {
        setValue(newValue);
      }
    })();
    // If this effect is cleaned up, don't set the data when it comes back!
    return () => {
      shouldSetValue = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return value;
};

export default useAwaitedValue;
